import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../../../contexts/ContentContext';
import { contentAPI } from '../../../api';
import './ContentDisplay.css';
import ContentCard from './ContentCard';
import { ContentLoadingSpinner, ContentErrorMessage, NoContent } from '../../../components/common/UI';

/**
 * 공지사항 아이템 컴포넌트
 */
const AnnouncementItem = memo(({ item, details }) => {
  const date = useMemo(() => {
    const d = details?.updated_at || item.content.updated_at;
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [details, item]);

  return (
    <div className="announcement-item">
      <Link to={`/content/announcement/${item.content.id}`} className="announcement-link">
        <div className="announcement-content">
          <span className="announcement-badge">공지</span>
          <h3 className="announcement-title">{details?.title || item.content.title}</h3>
        </div>
        <span className="announcement-date">{date}</span>
      </Link>
    </div>
  );
});

/**
 * 콘텐츠 표시 컴포넌트
 * 특정 섹션의 콘텐츠 목록을 표시합니다.
 */
const ContentDisplay = ({ section }) => {
  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  // 컨텍스트에서 데이터 가져오기
  const {
    orderedContent,
    contentDetails,
    lastFetched,
    updateContent
  } = useContent();

  // 캐시 유효 기간 (5분)
  const CACHE_DURATION = useMemo(() => 5 * 60 * 1000, []);

  // 콘텐츠 데이터 로딩
  useEffect(() => {
    if (fetchedRef.current) return;

    const shouldFetchData = () => {
      if (!lastFetched) return true;
      const timeSinceLastFetch = new Date() - new Date(lastFetched);
      return timeSinceLastFetch > CACHE_DURATION;
    };

    const fetchOrderedContent = async () => {
      if (!shouldFetchData() && orderedContent.length > 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await contentAPI.getSectionOrderedContent(section);
        const tempDetails = await contentAPI.fetchAllContentDetails(data.items);

        updateContent(data.items, tempDetails);
      } catch (error) {
        console.error('콘텐츠 목록 조회 실패:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
        fetchedRef.current = true;
      }
    };

    fetchOrderedContent();
  }, [section, lastFetched, orderedContent.length, updateContent, CACHE_DURATION]);

  // 콘텐츠 분류 (공지사항과 일반 콘텐츠)
  const renderContent = useMemo(() => {
    const announcements = orderedContent.filter(item => {
      const uniqueId = item.content.uniqueId || `${item.content.content_type}_${item.content.id}`;
      return contentDetails[uniqueId]?.content_type === 'announcement';
    });

    const otherContent = orderedContent.filter(item => {
      const uniqueId = item.content.uniqueId || `${item.content.content_type}_${item.content.id}`;
      return contentDetails[uniqueId]?.content_type !== 'announcement';
    });

    return { announcements, otherContent };
  }, [orderedContent, contentDetails]);

  // 로딩 및 에러 상태 처리
  if (isLoading) return <ContentLoadingSpinner />;
  if (error) return <ContentErrorMessage error={error} />;
  if (orderedContent.length === 0) return <NoContent />;

  const { announcements, otherContent } = renderContent;

  return (
    <div className="content-page-container" style={{ marginTop: '8%' }}>
      {/* 공지사항 섹션 */}
      {announcements.length > 0 && (
        <div className="announcements-section">
          <h2 className="section-title">공지사항</h2>
          <div className="announcements-list">
            {announcements.map((item) => {
              const uniqueId = item.content.uniqueId || `${item.content.content_type}_${item.content.id}`;
              return (
                <AnnouncementItem
                  key={uniqueId}
                  item={item}
                  details={contentDetails[uniqueId]}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* 일반 콘텐츠 섹션 */}
      <div className="main-content-section">
        <div className="content-grid">
          {otherContent.map((item) => {
            const uniqueId = item.content.uniqueId || `${item.content.content_type}_${item.content.id}`;
            return (
              <ContentCard
                key={uniqueId}
                content={item.content}
                details={contentDetails[uniqueId]}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(ContentDisplay);
